import type { GlobalUiState } from '@protector/entities/__legacy__/redux.types';
import { Spin } from 'antd';
import { getSystemFlags } from 'core/core.selectors';
import type { ReactNode } from 'react';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router';
import { ReloadErrorResult } from './reload-error-result.component';

type SystemFlagName = keyof NonNullable<GlobalUiState['systemFlags']>;

/*
 * The flags reach the store only once Rox calls its configuration handler, and nothing on a route
 * can make that happen again. Past this wait the reader gets a way out instead of a spinner that
 * never ends (P40-62528).
 */
const FLAGS_TIMEOUT_MS = 10_000;

export const FLAG_GATED_ROUTE_REDIRECT = '/hall-of-companies';

interface FlagGatedRouteProps {
  flag: SystemFlagName;
  /** Prefixes the loading and retry test ids, so each route keeps its own handles. */
  testId: string;
  children: ReactNode;
  redirectTo?: string;
  /** Rendered instead of redirecting when the flag is off, and, in place of the error, for good once the flags failed to arrive in time. */
  fallback?: ReactNode;
}

/**
 * A page that only exists while a flag says so. Waiting for the flags is not the flag being off, so
 * the two are answered apart: a cold store gets a spinner and then a way out, and only a flag that
 * has arrived and reads false sends the reader away.
 */
export const FlagGatedRoute = ({ flag, testId, children, redirectTo = FLAG_GATED_ROUTE_REDIRECT, fallback }: FlagGatedRouteProps) => {
  const { t } = useTranslation();
  const flags = useSelector(getSystemFlags);
  const [waitedTooLong, setWaitedTooLong] = useState(false);

  useEffect(() => {
    if (flags) {
      return undefined;
    }

    const timer = setTimeout(() => setWaitedTooLong(true), FLAGS_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [flags]);

  const loading = <Spin fullscreen aria-label={t('components.area_variable_info.loading')} data-testid={`${testId}-loading`} />;

  /* Late flags must not swap the page the reader is already using for another one. */
  if (waitedTooLong) {
    return fallback ?? <ReloadErrorResult testId={testId} />;
  }

  /* Not knowing the flags yet is not the flag being off: the store is empty on a cold load. */
  if (!flags) {
    return loading;
  }

  if (!flags[flag]) {
    return fallback ?? <Navigate to={redirectTo} replace />;
  }

  return <Suspense fallback={loading}>{children}</Suspense>;
};
